import {
  configureStore,
  createAction,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

export const resetAuth = createAction('auth/resetAuth');
export const resetUI = createAction('ui/resetUI');
export const resetFilters = createAction('filter/resetFilters');

type AuthState = {
  user: Record<string, unknown> | null;
  role: string | null;
  token: string | null;
};

const initialAuthState: AuthState = {
  user: null,
  role: null,
  token: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    setSession: (
      state,
      action: PayloadAction<{
        user: Record<string, unknown> | null;
        role: string | null;
        token: string | null;
      }>
    ) => {
      state.user = action.payload.user;
      state.role = action.payload.role;
      state.token = action.payload.token;
    },
    clearSession: (state) => {
      state.user = null;
      state.role = null;
      state.token = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetAuth, () => initialAuthState);
  },
});

type UiState = {
  theme: 'light' | 'dark';
  toasts: Array<{
    id: string;
    type: 'success' | 'error' | 'info';
    message: string;
    autoDismiss: boolean;
  }>;
};

const initialUiState: UiState = {
  theme: 'light',
  toasts: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUiState,
  reducers: {
    setTheme: (state, action: PayloadAction<UiState['theme']>) => {
      state.theme = action.payload;
    },
    addToast: (
      state,
      action: PayloadAction<{
        id?: string;
        type: 'success' | 'error' | 'info';
        message: string;
        autoDismiss?: boolean;
      }>
    ) => {
      const toast = {
        id: action.payload.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: action.payload.type,
        message: action.payload.message,
        autoDismiss:
          action.payload.autoDismiss ?? action.payload.type !== 'error',
      };
      state.toasts.push(toast);
      if (state.toasts.length > 3) {
        state.toasts.shift();
      }
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetUI, () => initialUiState);
  },
});

type FilterState = {
  activeSearchQuery: string;
  selectedFiltersByPage: Record<string, string[]>;
};

const initialFilterState: FilterState = {
  activeSearchQuery: '',
  selectedFiltersByPage: {},
};

const filterSlice = createSlice({
  name: 'filter',
  initialState: initialFilterState,
  reducers: {
    setActiveSearchQuery: (state, action: PayloadAction<string>) => {
      state.activeSearchQuery = action.payload;
    },
    setSelectedFiltersForPage: (
      state,
      action: PayloadAction<{ pageKey: string; filters: string[] }>
    ) => {
      state.selectedFiltersByPage[action.payload.pageKey] = action.payload.filters;
    },
    clearSelectedFiltersForPage: (state, action: PayloadAction<string>) => {
      delete state.selectedFiltersByPage[action.payload];
    },
    clearAllFilters: (state) => {
      state.activeSearchQuery = '';
      state.selectedFiltersByPage = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetFilters, () => initialFilterState);
  },
});

export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    ui: uiSlice.reducer,
    filter: filterSlice.reducer,
  },
});

export const { setSession, clearSession } = authSlice.actions;
export const {
  setTheme,
  addToast,
  removeToast,
  clearToasts,
} = uiSlice.actions;
export const {
  setActiveSearchQuery,
  setSelectedFiltersForPage,
  clearSelectedFiltersForPage,
  clearAllFilters,
} = filterSlice.actions;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
